import React, { useEffect, useMemo, useState } from "react";
import { useDrag, useDrop } from "react-dnd";
import { toast } from "react-toastify";
import AddTeamToGroupMutation from "../../components/stages/mutation/AddTeamToGroup";
import RemoveTeamFromGroupMutation from "../../components/stages/mutation/RemoveTeamFromGroup";
import { confirmDelete } from "../../components/common/confirmDelete";
import useThemeColors from "../../components/organization/portal/useThemeColors";
import { alphaFromHex } from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";

/**
 * ListTeams (new style system)
 * - Keeps your data flow (teams.status = "todo" or groupId)
 * - Uses same visual language as TeamList/StageList/Adjustment (rounded, soft borders, canary accents)
 * - Drop zones are visually clear; internal scroll for long lists
 */
const ListTeams = ({ teams, setTeams, groups, setGroups, stageId, seasonId }) => {
    const [todos, setTodos] = useState([]);
    const colors = useThemeColors();
    const { t } = useTranslation();

    useEffect(() => {
        const todoList = (teams || []).filter((team) => team.status === "todo");

        for (let i = 0; i < (groups || []).length; i += 1) {
            const group = groups[i];
            const teamsList = (teams || []).filter(
                (team) => String(team.status) === String(group.id)
            );

            setGroups((prev) =>
                (prev || []).map((prevGroup) =>
                    String(prevGroup.id) === String(group.id)
                        ? { ...prevGroup, teams: teamsList }
                        : prevGroup
                )
            );
        }

        setTodos(todoList);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teams]);

    const ui = useMemo(() => {
        return {
            grid: {
                display: "grid",
                gridTemplateColumns: "minmax(260px, 360px) 1fr",
                gap: 14,
            },
            leftCol: { minWidth: 0 },
            rightCol: { minWidth: 0 },

            groupsGrid: {
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(260px, 1fr))",
                gap: 14,
            },

            sectionTitle: {
                margin: "0 0 10px 0",
                fontSize: 14,
                fontWeight: 900,
                color: "#14151a",
            },
            sectionSub: {
                margin: "0 0 14px 0",
                fontSize: 12.5,
                fontWeight: 700,
                color: "rgba(20,21,26,0.55)",
            },

            card: {
                borderRadius: 18,
                overflow: "hidden",
                border: "1px solid rgba(0,0,0,0.08)",
                background: "#fff",
                boxShadow: "0 18px 44px rgba(0,0,0,0.08)",
            },
            header: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 10,
                padding: "12px 14px",
                background: `linear-gradient(
                    135deg,
                    ${alphaFromHex(colors.primary, 0.50)},
                    ${alphaFromHex(colors.primary, 0.0)}
                )`,
                borderBottom: "1px solid rgba(0,0,0,0.06)",
            },
            headerLeft: {
                display: "flex",
                alignItems: "center",
                gap: 10,
                minWidth: 0,
            },
            badge: {
                fontSize: 12,
                fontWeight: 900,
                letterSpacing: "0.03em",
                padding: "6px 10px",
                borderRadius: 999,
                background: colors.primary,
                border: "1px solid rgba(0,0,0,0.10)",
                color: colors.textOnPrimary,
                whiteSpace: "nowrap",
            },
            title: {
                margin: 0,
                fontSize: 13.5,
                fontWeight: 900,
                color: colors.textOnPrimary,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
            },
            actions: {
                display: "flex",
                alignItems: "center",
                gap: 8,
                flex: "0 0 auto",
            },

            actionBtn: {
                width: 38,
                height: 38,
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "#fff",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
            },
            actionBtnDanger: {
                border: "1px solid rgba(220,53,69,0.35)",
                background: "rgba(220,53,69,0.06)",
                color: "#dc3545",
            },

            body: { padding: 12, background: "#fff" },

            dropZone: (isOver, isTodo) => ({
                borderRadius: 14,
                border: `1px dashed ${isOver ? colors.primary : "rgba(0,0,0,0.14)"}`,
                background: isOver
                    ? colors.secondary
                    : isTodo
                        ? "rgba(0,0,0,0.012)"
                        : "rgba(0,0,0,0.008)",
                padding: 10,
                minHeight: 140,
                maxHeight: 420,
                overflowY: "auto",
                WebkitOverflowScrolling: "touch",
            }),

            list: {
                listStyle: "none",
                padding: 0,
                margin: 0,
                display: "flex",
                flexDirection: "column",
                gap: 8,
            },

            item: (isDragging, selected) => ({
                borderRadius: 14,
                border: `1px solid ${selected ? colors.primary : "rgba(0,0,0,0.10)"}`,
                background: isDragging ? "rgba(0,0,0,0.06)" : "#fff",
                padding: "10px 10px",
                display: "flex",
                alignItems: "center",
                gap: 10,
                cursor: "grab",
                userSelect: "none",
            }),
            handle: {
                width: 34,
                height: 34,
                borderRadius: 12,
                border: "1px solid rgba(0,0,0,0.10)",
                background: "rgba(0,0,0,0.02)",
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
                color: "rgba(20,21,26,0.70)",
                flex: "0 0 auto",
            },
            checkboxWrap: {
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
            },
            name: {
                fontWeight: 900,
                color: "#14151a",
                fontSize: 13.5,
                overflow: "hidden",
                textOverflow: "ellipsis",
            },
            muted: {
                color: "rgba(20,21,26,0.55)",
                fontWeight: 700,
                fontSize: 12.5,
            },

            footer: {
                padding: "12px 14px",
                borderTop: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.01)",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                gap: 10,
            },
            footerBtn: {
                borderRadius: 12,
                padding: "10px 12px",
                fontWeight: 900,
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                border: "1px solid rgba(0,0,0,0.12)",
                background: "#fff",
                color: "#14151a",
                cursor: "pointer",
                lineHeight: 1,
                whiteSpace: "nowrap",
            },

            empty: {
                borderRadius: 14,
                padding: 12,
                border: "1px solid rgba(0,0,0,0.06)",
                background: "rgba(0,0,0,0.012)",
                textAlign: "center",
                color: "rgba(20,21,26,0.60)",
                fontWeight: 800,
            },
        };
    }, [colors.primary, colors.secondary, colors.textOnPrimary]);

    return (
        <div id="kbola-groups-dnd">
            <div style={ui.sectionTitle}>{t("listTeams.section.title")}</div>
            <div style={ui.sectionSub}>{t("listTeams.section.subtitle")}</div>

            <div style={ui.grid} className="kbola-groupsGrid">
                <div style={ui.leftCol}>
                    <TaskContainer
                        teams={todos}
                        title={t("listTeams.containers.teams")}
                        setTeams={setTeams}
                        status="todo"
                        setGroups={setGroups}
                        stageId={stageId}
                        seasonId={seasonId}
                        ui={ui}
                    />
                </div>

                <div style={ui.rightCol}>
                    <div style={ui.groupsGrid} className="kbola-groupsCardsGrid">
                        {(groups || []).map((group) => (
                            <TaskContainer
                                key={group.id}
                                teams={group.teams}
                                setTeams={setTeams}
                                status={group.id}
                                title={group.name}
                                setGroups={setGroups}
                                stageId={stageId}
                                seasonId={seasonId}
                                ui={ui}
                            />
                        ))}
                    </div>
                </div>
            </div>

            <style>{`
                #kbola-groups-dnd .kbola-groupsGrid{
                  align-items: start;
                }

                @media (max-width: 980px){
                  #kbola-groups-dnd .kbola-groupsGrid{
                    grid-template-columns: 1fr !important;
                  }
                }

                @media (max-width: 820px){
                  #kbola-groups-dnd .kbola-groupsCardsGrid{
                    grid-template-columns: 1fr !important;
                  }
                }

                #kbola-groups-dnd button:focus{
                  outline: none;
                  box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
                }

                #kbola-groups-dnd input[type="checkbox"]:focus{
                  outline: none;
                  box-shadow: 0 0 0 0.18rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
                }
            `}</style>
        </div>
    );
};

export default ListTeams;

const TaskContainer = ({
                           teams,
                           setTeams,
                           status,
                           title,
                           setGroups,
                           stageId,
                           seasonId,
                           ui,
                       }) => {
    const { t } = useTranslation();

    const [{ isOver }, drop] = useDrop(
        () => ({
            accept: "team",
            drop: (item) => addItemToColumn(item.id),
            collect: (monitor) => ({ isOver: !!monitor.isOver() }),
        }),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [status, stageId, seasonId]
    );

    const [addTeamToGroup, { loading: loadingAdd }] = AddTeamToGroupMutation();
    const [removeTeamFromGroup] = RemoveTeamFromGroupMutation();

    const addItemToColumn = (id) => {
        if (loadingAdd) {
            toast.info(t("listTeams.toasts.addingTeamToGroup"), {
                autoClose: 1500,
                toastId: "adding_team",
                position: "top-center",
                closeOnClick: true,
            });
        }

        setTeams((prevTeams) => {
            return (prevTeams || []).map((team) => {
                if (team.id !== id) return team;

                if (status === "todo") {
                    removeTeamFromGroup({
                        variables: {
                            stageId: parseInt(stageId, 10),
                            seasonId: parseInt(seasonId, 10),
                            teamId: parseInt(id, 10),
                            groupId: team.status,
                        },
                    }).catch((err) => {
                        toast.error(
                            err?.message || t("listTeams.toasts.failedToRemoveTeamFromGroup"),
                            {
                                autoClose: 3000,
                                toastId: "remove_team_error",
                                position: "top-center",
                                closeOnClick: true,
                            }
                        );
                    });
                } else {
                    addTeamToGroup({
                        variables: {
                            stageId: parseInt(stageId, 10),
                            seasonId: parseInt(seasonId, 10),
                            groups: [
                                {
                                    groupId: status,
                                    teamId: parseInt(id, 10),
                                    teamAliasName: id,
                                },
                            ],
                        },
                    }).catch((err) => {
                        toast.error(
                            err?.message || t("listTeams.toasts.failedToAddTeamToGroup"),
                            {
                                autoClose: 3000,
                                toastId: "add_team_error",
                                position: "top-center",
                                closeOnClick: true,
                            }
                        );
                    });
                }

                return { ...team, status, selected: false };
            });
        });
    };

    const removeSelectedTeamsFromGroup = (groupId) => {
        setTeams((prevTeams) => {
            return (prevTeams || []).map((team) => {
                if (!team.selected) return team;

                removeTeamFromGroup({
                    variables: {
                        stageId: parseInt(stageId, 10),
                        seasonId: parseInt(seasonId, 10),
                        teamId: parseInt(team.id, 10),
                        groupId,
                    },
                }).catch((err) => {
                    toast.error(err?.message || t("listTeams.toasts.failedToRemoveTeams"), {
                        autoClose: 3000,
                        toastId: "remove_team_error",
                        position: "top-center",
                        closeOnClick: true,
                    });
                });

                return { ...team, status: "todo", selected: false };
            });
        });
    };

    const removeGroup = (groupId) => {
        setTeams((prevTeams) =>
            (prevTeams || []).map((team) =>
                String(team.status) === String(groupId)
                    ? { ...team, status: "todo" }
                    : team
            )
        );

        setGroups((prevGroups) =>
            (prevGroups || []).filter(
                (group) => String(group.id) !== String(groupId)
            )
        );

        removeTeamFromGroup({
            variables: {
                stageId: parseInt(stageId, 10),
                seasonId: parseInt(seasonId, 10),
                groupId,
                deleteGroup: true,
            },
        }).catch((err) => {
            toast.error(err?.message || t("listTeams.toasts.failedToDeleteGroup"), {
                autoClose: 3000,
                toastId: "remove_group_error",
                position: "top-center",
                closeOnClick: true,
            });
        });
    };

    const deleteGroupConfirm = (groupId) => {
        confirmDelete({
            title: t("listTeams.confirm.deleteGroupTitle"),
            message: t("listTeams.confirm.deleteGroupMessage"),
            confirmLabel: t("listTeams.confirm.deleteGroupConfirmLabel"),
            cancelLabel: t("listTeams.confirm.cancel"),
            danger: true,
            onConfirm: async () => removeGroup(groupId),
        });
    };

    const isTodo = status === "todo";

    return (
        <div style={ui.card}>
            <div style={ui.header}>
                <div style={ui.headerLeft}>
                    <span style={ui.badge}>{teams?.length || 0}</span>
                    <h3 style={ui.title} title={title}>
                        {title}
                    </h3>
                </div>

                {!isTodo && (
                    <div style={ui.actions}>
                        <button
                            type="button"
                            style={{ ...ui.actionBtn, ...ui.actionBtnDanger }}
                            onClick={() => deleteGroupConfirm(status)}
                            title={t("listTeams.buttons.deleteGroup")}
                        >
                            <i className="fas fa-trash" />
                        </button>
                    </div>
                )}
            </div>

            <div style={ui.body}>
                <div
                    ref={drop}
                    style={ui.dropZone(isOver, isTodo)}
                    aria-label={
                        isTodo
                            ? t("listTeams.containers.teamsListAria")
                            : t("listTeams.containers.groupDropZoneAria")
                    }
                >
                    {teams?.length ? (
                        <ul style={ui.list}>
                            {teams.map((team) => (
                                <Team
                                    key={team.id}
                                    team={team}
                                    setTeams={setTeams}
                                    ui={ui}
                                />
                            ))}
                        </ul>
                    ) : (
                        <div style={ui.empty}>
                            {isTodo
                                ? t("listTeams.containers.noUnassignedTeams")
                                : t("listTeams.containers.dropTeamsHere")}
                        </div>
                    )}
                </div>
            </div>

            {!isTodo && (
                <div style={ui.footer}>
                    <button
                        type="button"
                        style={ui.footerBtn}
                        onClick={() => removeSelectedTeamsFromGroup(status)}
                    >
                        <i className="fas fa-trash" />
                        {t("listTeams.buttons.removeSelected")}
                    </button>
                </div>
            )}
        </div>
    );
};

const Team = ({ team, setTeams, ui }) => {
    const { t } = useTranslation();

    const [{ isDragging }, drag] = useDrag(() => ({
        type: "team",
        item: { id: team.id },
        collect: (monitor) => ({ isDragging: !!monitor.isDragging() }),
    }));

    const handleCheck = (e) => {
        const id = parseInt(e.target.value, 10);
        const selected = !!e.target.checked;

        setTeams((prev) =>
            (prev || []).map((item) =>
                item.id === id ? { ...item, selected } : item
            )
        );
    };

    return (
        <li ref={drag} style={ui.item(isDragging, !!team.selected)}>
            <span style={ui.handle} title={t("listTeams.misc.drag")}>
                <i className="fas fa-grip-vertical" />
            </span>

            <span style={ui.checkboxWrap}>
                <input
                    type="checkbox"
                    value={team.id}
                    id={`teamCheck_${team.id}`}
                    checked={!!team.selected}
                    onChange={handleCheck}
                />
            </span>

            <div style={{ minWidth: 0 }}>
                <div style={ui.name}>{team.name}</div>
                <div style={ui.muted}>
                    {team.status === "todo"
                        ? t("listTeams.status.unassigned")
                        : t("listTeams.status.assignedToGroup")}
                </div>
            </div>
        </li>
    );
};